/**
 * civil-registration-groups.ts — Wave 77 (บริการที่ 21: เอกสารทะเบียนราษฎรและงานทะเบียนบุคคล)
 *
 * แหล่งอ้างอิงที่ตรวจสอบได้เท่านั้น:
 *   - พระราชบัญญัติการทะเบียนราษฎร พ.ศ. 2534 (และที่แก้ไขเพิ่มเติม) — การแจ้งเกิด แจ้งตาย
 *     ทะเบียนบ้าน และการออกหนังสือรับรองรายการทะเบียนราษฎรโดยนายทะเบียน
 *   - พระราชบัญญัติบัตรประจำตัวประชาชน พ.ศ. 2526 (และที่แก้ไขเพิ่มเติม) — การออกบัตรและ
 *     หนังสือรับรองรายการบัตรประจำตัวประชาชน
 *   - พระราชบัญญัติชื่อบุคคล พ.ศ. 2505 (และที่แก้ไขเพิ่มเติม) — การเปลี่ยนชื่อตัวและชื่อสกุล
 *     ซึ่งนายทะเบียนออกหลักฐานเป็นหนังสือสำคัญ (แบบ ช.3 / ช.5)
 *   - กรมการปกครอง กระทรวงมหาดไทย (bora.dopa.go.th) — สำนักทะเบียนอำเภอและสำนักทะเบียนท้องถิ่น
 *     เป็นผู้ออกและรับรองสำเนาเอกสารทะเบียนราษฎร
 *   - กรมการกงสุล กระทรวงการต่างประเทศ (consular.mfa.go.th) — งานนิติกรณ์รับรองเอกสารและคำแปล
 *   - สภาทนายความในพระบรมราชูปถัมภ์ — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney)
 *   - อนุสัญญากรุงเฮก ค.ศ. 1961 (Apostille) — สถานะของไทยตาม HCCH (src/data/apostille-status.ts)
 *
 * กติกาเนื้อหา: ไม่ระบุราคา ไม่รับประกันผลการพิจารณาของนายทะเบียน สถานทูต หรือหน่วยงานปลายทาง
 * และไม่อ้างค่าธรรมเนียมหรือระยะเวลาทำการของหน่วยงานอื่นโดยไม่มีที่มา
 */
import { APOSTILLE_STATUS } from "@/data/apostille-status";

export interface CivilRegGroup {
  slug: string;
  titleTh: string;
  titleEn: string;
  summaryTh: string;
  summaryEn: string;
  bodyTh: string[];
  bodyEn: string[];
  documentsTh: string[];
  documentsEn: string[];
  chainTh: string[];
  chainEn: string[];
  checklistTh: string[];
  checklistEn: string[];
  tipsTh: string[];
  tipsEn: string[];
  pitfallsTh: string[];
  pitfallsEn: string[];
  faqsTh: { q: string; a: string }[];
  faqsEn: { q: string; a: string }[];
}

export const CIVILREG_APOSTILLE_TH =
  `สถานะกฎหมาย: ไทยยื่นภาคยานุวัติสารเข้าเป็นภาคีอนุสัญญากรุงเฮก ค.ศ. 1961 (Apostille) เมื่อ ` +
  `${APOSTILLE_STATUS.accessionDepositTh} และอนุสัญญาจะมีผลบังคับใช้กับประเทศไทยในวันที่ ` +
  `${APOSTILLE_STATUS.entryIntoForceTh} (ที่มา: HCCH) ก่อนวันดังกล่าว เอกสารทะเบียนราษฎรของไทย เช่น ` +
  `สูติบัตร ทะเบียนบ้าน มรณบัตร หรือหนังสือสำคัญการเปลี่ยนชื่อ ที่จะใช้ต่อหน่วยงานต่างประเทศ ` +
  `ยังต้องผ่านลำดับเดิม คือขอสำเนาที่นายทะเบียนรับรอง แปลเป็นภาษาที่ปลายทางกำหนด ` +
  `รับรองคำแปลที่กรมการกงสุล แล้วจึงนำไปรับรองต่อสถานเอกอัครราชทูตของประเทศปลายทางเมื่อประเทศนั้นกำหนด`;

export const CIVILREG_APOSTILLE_EN =
  `Legal status: Thailand deposited its instrument of accession to the 1961 Hague Apostille Convention on ` +
  `${APOSTILLE_STATUS.accessionDepositEn}; it enters into force for Thailand on ${APOSTILLE_STATUS.entryIntoForceEn} ` +
  `(source: HCCH). Until then, Thai civil-registration records — a birth certificate, a house registration book, ` +
  `a death certificate or a change-of-name certificate — still follow the existing route: a registrar-certified ` +
  `copy, a translation into the language the receiver requires, legalisation of that translation by the ` +
  `Department of Consular Affairs, and, where the destination country requires it, endorsement by that country's ` +
  `embassy in Bangkok.`;

export const CIVILREG_AUTHORITY_TH =
  `หน่วยงานที่รับผิดชอบแยกกันชัดเจน: เอกสารทะเบียนราษฎรทุกฉบับออกและรับรองสำเนาโดยนายทะเบียน ` +
  `ที่สำนักทะเบียนอำเภอหรือสำนักทะเบียนท้องถิ่นเทศบาล/สำนักงานเขต ตามพระราชบัญญัติการทะเบียนราษฎร พ.ศ. 2534 ` +
  `ไม่มีสำนักงานกฎหมายหรือบริษัทแปลใดออกเอกสารเหล่านี้แทนรัฐได้ และการแก้ไขรายการในทะเบียนต้องยื่นต่อนายทะเบียนเท่านั้น ` +
  `บทบาทของเราเริ่มหลังจากได้เอกสารที่นายทะเบียนรับรองแล้ว คือแปลให้ตรงตามต้นฉบับ ` +
  `รับรองลายมือชื่อและสำเนาโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารเมื่อปลายทางกำหนด ` +
  `และเดินเรื่องนิติกรณ์ที่กรมการกงสุลและสถานทูต`;

export const CIVILREG_AUTHORITY_EN =
  `Responsibilities are separated. Every civil-registration record is issued, and its copies certified, by the ` +
  `registrar at a district office or local registration office under the Civil Registration Act B.E. 2534. No law ` +
  `firm or translation company can issue these documents in place of the state, and any correction to a register ` +
  `entry must be filed with the registrar. Our role begins once you hold the registrar-certified document: an ` +
  `accurate translation, signature and copy certification by a Notarial Services Attorney where the receiver asks ` +
  `for it, and legalisation at the Department of Consular Affairs and the relevant embassy.`;

export const CIVIL_REG_GROUPS: CivilRegGroup[] = [
  {
    slug: "birth-certificate",
    titleTh: "สูติบัตรและหนังสือรับรองการเกิด",
    titleEn: "Birth certificate and birth records",
    summaryTh:
      "สูติบัตรออกโดยนายทะเบียนหลังการแจ้งเกิดตามพระราชบัญญัติการทะเบียนราษฎร พ.ศ. 2534 ใช้เป็นหลักฐานยืนยันตัวบุคคล สัญชาติ และความสัมพันธ์บิดามารดา สำหรับวีซ่าติดตามครอบครัว สมัครเรียน และงานทะเบียนในต่างประเทศ",
    summaryEn:
      "A birth certificate is issued by the registrar after a birth is notified under the Civil Registration Act B.E. 2534. It proves identity, nationality and parentage for dependant visas, school admission and civil-status filings abroad.",
    bodyTh: [
      "การแจ้งเกิดในประเทศไทยทำที่สำนักทะเบียนในเขตพื้นที่ที่เด็กเกิด โดยใช้หนังสือรับรองการเกิด (ท.ร.1/1) ที่สถานพยาบาลออกให้เป็นหลักฐานประกอบ นายทะเบียนจะบันทึกรายการลงในทะเบียนคนเกิดแล้วออกสูติบัตรให้ผู้แจ้ง เอกสารฉบับนี้จึงเป็นเอกสารราชการที่ปลายทางในต่างประเทศยอมรับ ส่วนหนังสือรับรองการเกิดจากโรงพยาบาลเพียงอย่างเดียวมักไม่พอสำหรับงานวีซ่าหรืองานทะเบียน",
      "หากสูติบัตรฉบับจริงสูญหายหรือชำรุด ให้ขอคัดและรับรองสำเนารายการในทะเบียนคนเกิดจากสำนักทะเบียนที่แจ้งเกิดไว้ สำเนาที่นายทะเบียนลงนามรับรองใช้แทนฉบับจริงได้ในงานนิติกรณ์ ทั้งนี้สำเนาที่ถ่ายเอกสารเองโดยไม่มีลายมือชื่อนายทะเบียนจะไม่ผ่านขั้นตอนรับรองที่กรมการกงสุล",
      "การใช้สูติบัตรในต่างประเทศต้องผ่านการแปลที่ถ่ายทอดรายการให้ครบทุกช่อง รวมถึงเลขประจำตัวประชาชน สถานที่เกิด และรายการบิดามารดา ชื่อบุคคลในคำแปลต้องสะกดตรงกับหนังสือเดินทางฉบับที่จะใช้ยื่นจริง เพราะหน่วยงานปลายทางเทียบตัวสะกดกับหนังสือเดินทางเป็นหลัก",
      "เด็กที่เกิดในต่างประเทศจากบิดาหรือมารดาสัญชาติไทยจะแจ้งเกิดที่สถานเอกอัครราชทูตหรือสถานกงสุลใหญ่ไทย และได้รับสูติบัตรไทยที่ออกโดยฝ่ายกงสุล ซึ่งใช้ในการเพิ่มชื่อเข้าทะเบียนบ้านในไทยและขอหนังสือเดินทางไทยได้ ควรตรวจสอบเงื่อนไขกับสถานทูตไทยในประเทศนั้นก่อนยื่น เพราะเอกสารประกอบต่างกันตามกฎหมายท้องถิ่นของแต่ละประเทศ",
    ],
    bodyEn: [
      "A birth in Thailand is notified at the registration office covering the place of birth, supported by the hospital's certificate of birth (Thor Ror 1/1). The registrar records the entry in the birth register and issues the birth certificate, which is the state document foreign authorities accept. A hospital certificate alone is usually not sufficient for visa or civil-status filings.",
      "If the original is lost or damaged, request a registrar-certified extract of the birth register from the office where the birth was notified. A copy signed by the registrar can be used in place of the original for legalisation; a self-made photocopy without the registrar's signature will not pass consular legalisation.",
      "For use abroad, the translation must carry every field, including the national identification number, the place of birth and the parents' particulars. Personal names must be spelled exactly as in the passport that will be filed, because receiving authorities match spellings against the passport first.",
      "A child born abroad to a Thai parent is registered at the Thai embassy or consulate-general, which issues a Thai birth certificate. That document supports adding the child to a Thai house registration and applying for a Thai passport. Confirm the supporting documents with the Thai mission in that country first, as they vary with local law.",
    ],
    documentsTh: [
      "สูติบัตรฉบับจริง หรือสำเนาที่นายทะเบียนลงนามรับรอง",
      "บัตรประจำตัวประชาชนหรือหนังสือเดินทางของเจ้าของเอกสาร",
      "บัตรประจำตัวประชาชนของบิดามารดา กรณีเจ้าของเอกสารยังไม่บรรลุนิติภาวะ",
      "หนังสือเดินทางฉบับที่จะใช้ยื่น เพื่อยืนยันตัวสะกดชื่อในคำแปล",
      "หนังสือมอบอำนาจ กรณีให้ผู้อื่นดำเนินการแทนในขั้นตอนนิติกรณ์",
    ],
    documentsEn: [
      "The original birth certificate, or a copy signed and certified by the registrar",
      "National ID card or passport of the document holder",
      "Parents' ID cards where the holder is a minor",
      "The passport that will be filed, to fix the spelling of names in the translation",
      "A power of attorney if a representative will handle the legalisation steps",
    ],
    chainTh: [
      "ขอสูติบัตรฉบับจริงหรือสำเนารับรองจากสำนักทะเบียนที่แจ้งเกิด",
      "แปลเป็นภาษาที่หน่วยงานปลายทางกำหนด โดยถ่ายทอดครบทุกรายการ",
      "รับรองสำเนาหรือลายมือชื่อโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร เมื่อปลายทางกำหนด",
      "ยื่นรับรองคำแปลที่กองสัญชาติและนิติกรณ์ กรมการกงสุล",
      "นำไปรับรองต่อสถานเอกอัครราชทูตของประเทศปลายทาง เมื่อประเทศนั้นกำหนด",
    ],
    chainEn: [
      "Obtain the original or a registrar-certified copy from the office where the birth was notified",
      "Translate into the language the receiver requires, carrying over every field",
      "Have the copy or signature certified by a Notarial Services Attorney where required",
      "File the translation for legalisation at the Department of Consular Affairs",
      "Present the legalised set to the destination country's embassy where that country requires it",
    ],
    checklistTh: [
      "ตัวสะกดชื่อในคำแปลตรงกับหนังสือเดินทางฉบับที่จะใช้ยื่น",
      "เลขประจำตัวประชาชน 13 หลักในคำแปลตรงกับต้นฉบับทุกหลัก",
      "รายการบิดามารดาครบถ้วน ไม่ตัดคำนำหน้าหรือชื่อสกุลเดิม",
      "สำเนามีลายมือชื่อและตราของนายทะเบียน ไม่ใช่สำเนาถ่ายเอง",
      "ตรวจสอบว่าปลายทางรับเอกสารอายุไม่เกินกี่เดือนก่อนขอคัดสำเนาใหม่",
    ],
    checklistEn: [
      "Name spellings in the translation match the passport that will be filed",
      "The 13-digit national ID number is reproduced digit for digit",
      "Parents' particulars are complete, including titles and any former surname",
      "The copy bears the registrar's signature and seal, not a self-made photocopy",
      "Check how recent the receiver requires the copy to be before ordering a fresh extract",
    ],
    tipsTh: [
      "ขอคัดสำเนารับรองเผื่อไว้มากกว่าหนึ่งชุด เพราะบางขั้นตอนต้องยึดเอกสารไว้กับแฟ้ม",
      "ถ่ายภาพต้นฉบับทุกหน้าก่อนส่งเอกสารเข้าสู่ขั้นตอนนิติกรณ์",
      "หากเคยเปลี่ยนชื่อหรือชื่อสกุล ให้เตรียมหนังสือสำคัญการเปลี่ยนชื่อแนบไปพร้อมกันเสมอ",
      "สอบถามหน่วยงานปลายทางว่าต้องการคำแปลภาษาอังกฤษหรือภาษาราชการของประเทศนั้นก่อนเริ่มแปล",
    ],
    tipsEn: [
      "Order more than one certified copy — some steps keep a copy on file",
      "Photograph every page of the original before it enters the legalisation chain",
      "If a name has ever been changed, always attach the change-of-name certificate as well",
      "Ask the receiver whether it wants an English translation or the local official language before translating",
    ],
    pitfallsTh: [
      "ใช้หนังสือรับรองการเกิดของโรงพยาบาลแทนสูติบัตร ทำให้ปลายทางปฏิเสธ",
      "คำแปลสะกดชื่อคนละแบบกับหนังสือเดินทาง ทำให้ต้องแก้และยื่นใหม่",
      "สำเนาถ่ายเองไม่มีลายมือชื่อนายทะเบียน จึงไม่ผ่านการรับรองคำแปล",
      "ลืมแนบหลักฐานการเปลี่ยนชื่อ ทำให้ชื่อในสูติบัตรกับเอกสารปัจจุบันไม่ตรงกัน",
    ],
    pitfallsEn: [
      "Submitting the hospital's certificate of birth instead of the registrar's birth certificate",
      "Name spelling in the translation differing from the passport, forcing a refiling",
      "A self-made photocopy without a registrar's signature failing at legalisation",
      "Omitting the change-of-name certificate so the birth record and current documents disagree",
    ],
    faqsTh: [
      {
        q: "สูติบัตรหายต้องทำอย่างไร",
        a: "ติดต่อสำนักทะเบียนที่แจ้งเกิดเพื่อขอคัดและรับรองสำเนารายการในทะเบียนคนเกิด สำเนาที่นายทะเบียนลงนามรับรองใช้ในงานแปลและงานนิติกรณ์ได้เช่นเดียวกับฉบับจริง",
      },
      {
        q: "หนังสือรับรองการเกิดจากโรงพยาบาลใช้แทนสูติบัตรได้ไหม",
        a: "โดยทั่วไปใช้แทนไม่ได้ เพราะเป็นเอกสารของสถานพยาบาลที่ใช้ประกอบการแจ้งเกิดเท่านั้น เอกสารที่หน่วยงานต่างประเทศต้องการคือสูติบัตรที่นายทะเบียนออกตามพระราชบัญญัติการทะเบียนราษฎร พ.ศ. 2534",
      },
      {
        q: "ต้องแปลเป็นภาษาอังกฤษเสมอหรือไม่",
        a: "ขึ้นกับหน่วยงานปลายทาง บางประเทศรับคำแปลภาษาอังกฤษ บางประเทศกำหนดให้แปลเป็นภาษาราชการของตน ควรยืนยันกับสถานทูตหรือหน่วยงานที่รับเรื่องก่อนเริ่มแปล",
      },
      {
        q: "สูติบัตรมีอายุการใช้งานหรือไม่",
        a: "ตัวเอกสารไม่มีวันหมดอายุ แต่หน่วยงานปลายทางหลายแห่งกำหนดว่าสำเนารับรองต้องออกใหม่ภายในระยะเวลาที่กำหนด จึงควรตรวจสอบเงื่อนไขของปลายทางก่อนยื่น",
      },
      {
        q: "เด็กเกิดต่างประเทศจะขอสูติบัตรไทยได้ที่ไหน",
        a: "แจ้งเกิดที่สถานเอกอัครราชทูตหรือสถานกงสุลใหญ่ไทยในประเทศที่เด็กเกิด เอกสารประกอบต่างกันตามกฎหมายท้องถิ่น จึงควรสอบถามสถานทูตไทยในประเทศนั้นก่อน",
      },
      {
        q: "ทางบริษัทช่วยขอสูติบัตรจากสำนักทะเบียนแทนได้หรือไม่",
        a: "การคัดสำเนาทะเบียนราษฎรเป็นอำนาจของนายทะเบียนและมีเงื่อนไขเรื่องผู้มีสิทธิยื่น เราช่วยได้ในขั้นตอนหลังจากนั้น คือการแปล การรับรองโดยทนายความ และการเดินเรื่องนิติกรณ์ สอบถามรายละเอียดได้ทาง LINE @nycli โทร +66-83-249-4999 หรืออีเมล nycli@ilc.ltd",
      },
    ],
    faqsEn: [
      {
        q: "What if the birth certificate is lost?",
        a: "Contact the registration office where the birth was notified and request a certified extract of the birth register. A copy signed by the registrar works for translation and legalisation exactly like the original.",
      },
      {
        q: "Can the hospital's certificate of birth replace the birth certificate?",
        a: "Generally no. It is a healthcare-facility document used only to notify the birth. Foreign authorities want the registrar's birth certificate issued under the Civil Registration Act B.E. 2534.",
      },
      {
        q: "Does it always have to be translated into English?",
        a: "Document translation into English depends on the receiving authority's specific requirements. While some countries accept English translations, others require documents in their own official language. You should always confirm the exact requirements with the embassy or receiving authority before beginning the translation process.",
      },
      {
        q: "Does a birth certificate expire?",
        a: "The document itself does not expire, but many receivers require a certified copy issued within a set recent period, so check the receiver's rule before filing.",
      },
      {
        q: "Where can a child born abroad obtain a Thai birth certificate?",
        a: "The birth is registered at the Thai embassy or consulate-general in the country of birth. Supporting documents vary with local law, so ask that mission first.",
      },
      {
        q: "Can you obtain the certificate from the registrar on our behalf?",
        a: "Extracting civil-registration records is the registrar's authority and is limited to eligible applicants. We assist with everything after that: translation, attorney certification and legalisation. Contact LINE @nycli, +66-83-249-4999 or nycli@ilc.ltd.",
      },
    ],
  },
  {
    slug: "house-registration",
    titleTh: "ทะเบียนบ้านและหนังสือรับรองที่อยู่",
    titleEn: "House registration and address certificates",
    summaryTh:
      "ทะเบียนบ้าน (ท.ร.14) เป็นหลักฐานแสดงรายการบุคคลในบ้านตามพระราชบัญญัติการทะเบียนราษฎร พ.ศ. 2534 ใช้ยืนยันที่อยู่ ความสัมพันธ์ในครอบครัว และประกอบงานวีซ่า ธนาคาร และงานทะเบียนในต่างประเทศ",
    summaryEn:
      "The house registration book (Thor Ror 14) records the persons registered at an address under the Civil Registration Act B.E. 2534. It evidences residence and family links for visa files, banks and civil filings abroad.",
    bodyTh: [
      "ทะเบียนบ้านแสดงเลขรหัสประจำบ้าน ที่ตั้ง และรายการของผู้มีชื่ออยู่ในบ้าน พร้อมสถานะเจ้าบ้านหรือผู้อาศัย เมื่อจะใช้ต่างประเทศ ปลายทางมักขอสำเนาที่นายทะเบียนรับรองเฉพาะหน้าที่มีรายการของผู้ยื่นและหน้าที่แสดงที่ตั้งบ้าน ไม่จำเป็นต้องส่งทั้งเล่มเสมอไป",
      "คนต่างชาติที่มีชื่ออยู่ในทะเบียนบ้านจะอยู่ในเล่ม ท.ร.13 ซึ่งเป็นทะเบียนบ้านสำหรับบุคคลที่เข้าเมืองโดยชอบด้วยกฎหมายแต่ยังไม่ได้สัญชาติไทย ควรระบุให้ชัดเมื่อสั่งแปล เพราะรูปแบบรายการและหัวเรื่องต่างจาก ท.ร.14",
      "หนังสือรับรองที่อยู่หรือหนังสือรับรองรายการทะเบียนราษฎรที่นายทะเบียนออกให้ ใช้ในกรณีที่ปลายทางต้องการเอกสารที่ระบุที่อยู่ปัจจุบันเป็นภาษาราชการพร้อมตราครุฑ ซึ่งชัดเจนกว่าการส่งสำเนาหน้าทะเบียนบ้านอย่างเดียว ผู้ยื่นควรถามปลายทางว่าต้องการรูปแบบใด",
      "งานที่พบบ่อยคือการใช้ทะเบียนบ้านประกอบการยื่นวีซ่าติดตามครอบครัว การเปิดบัญชีธนาคารในต่างประเทศ และการยืนยันความสัมพันธ์บิดามารดา-บุตรควบคู่กับสูติบัตร กรณีเช่นนี้ควรจัดชุดเอกสารให้สอดคล้องกัน ทั้งชื่อ ที่อยู่ และวันที่ออกเอกสาร",
    ],
    bodyEn: [
      "The house registration shows the house code, the address and the persons registered there with their status as householder or occupant. For overseas use, receivers usually want a registrar-certified copy of the page listing the applicant and the page showing the address — not always the whole book.",
      "Foreign nationals lawfully resident but without Thai nationality are recorded in a Thor Ror 13 book. Say so when ordering a translation: its headings and fields differ from the Thor Ror 14.",
      "A residence certificate, or a certificate of civil-registration particulars issued by the registrar, suits receivers that want the current address stated on an official letterhead rather than a page copy. Ask the receiver which format it expects.",
      "Common uses are dependant-visa files, opening a bank account abroad, and proving a parent-child relationship alongside a birth certificate. In those files the names, address and issue dates across all documents should agree.",
    ],
    documentsTh: [
      "ทะเบียนบ้านฉบับเจ้าบ้าน หรือสำเนาที่นายทะเบียนลงนามรับรอง",
      "บัตรประจำตัวประชาชนของผู้มีชื่อในทะเบียนบ้าน",
      "หนังสือเดินทางฉบับที่จะใช้ยื่น เพื่อยืนยันตัวสะกดชื่อและที่อยู่ในคำแปล",
      "หนังสือรับรองรายการทะเบียนราษฎรจากสำนักทะเบียน กรณีปลายทางขอเอกสารรูปแบบหนังสือรับรอง",
      "หนังสือมอบอำนาจ กรณีให้ผู้อื่นดำเนินการแทน",
    ],
    documentsEn: [
      "The householder's house registration book, or a registrar-certified copy",
      "National ID card of the person listed in the book",
      "The passport to be filed, to fix name and address spellings in the translation",
      "A certificate of civil-registration particulars where the receiver wants a certificate format",
      "A power of attorney if a representative will act for you",
    ],
    chainTh: [
      "ขอสำเนาหน้าที่เกี่ยวข้องพร้อมลายมือชื่อรับรองของนายทะเบียน",
      "แปลรายการทั้งหมดในหน้าที่ใช้ รวมถึงเลขรหัสประจำบ้านและสถานะในบ้าน",
      "รับรองสำเนาหรือลายมือชื่อโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร เมื่อปลายทางกำหนด",
      "ยื่นรับรองคำแปลที่กรมการกงสุล",
      "รับรองต่อสถานเอกอัครราชทูตของประเทศปลายทาง เมื่อประเทศนั้นกำหนด",
    ],
    chainEn: [
      "Obtain certified copies of the relevant pages, signed by the registrar",
      "Translate every field on those pages, including the house code and the residence status",
      "Add attorney certification of the copy or signature where the receiver requires it",
      "File the translation for legalisation at the Department of Consular Affairs",
      "Endorse at the destination country's embassy where that country requires it",
    ],
    checklistTh: [
      "หน้าที่ส่งแปลมีทั้งรายการบุคคลและหน้าที่แสดงที่ตั้งบ้าน",
      "ที่อยู่ในคำแปลเขียนตามลำดับที่ปลายทางอ่านเข้าใจ และตรงกับต้นฉบับ",
      "ระบุให้ชัดว่าเป็น ท.ร.14 หรือ ท.ร.13 ก่อนเริ่มแปล",
      "ชื่อผู้ยื่นในทะเบียนบ้านตรงกับหนังสือเดินทางและเอกสารอื่นในชุดเดียวกัน",
      "สำเนาทุกหน้ามีลายมือชื่อและตราของนายทะเบียน",
    ],
    checklistEn: [
      "The pages sent for translation cover both the person entry and the address page",
      "The address in the translation is written so the receiver can read it and matches the original",
      "The book type — Thor Ror 14 or Thor Ror 13 — is stated before translation starts",
      "The applicant's name matches the passport and the other documents in the same file",
      "Every copied page carries the registrar's signature and seal",
    ],
    tipsTh: [
      "ถ้าปลายทางขอ proof of address ให้ถามว่ารับสำเนาทะเบียนบ้านหรือขอหนังสือรับรองจากนายทะเบียน",
      "จัดวันที่ออกเอกสารในชุดเดียวกันให้ใกล้เคียงกัน ลดคำถามเรื่องความเป็นปัจจุบัน",
      "ถ้าย้ายที่อยู่ระหว่างดำเนินเรื่อง ให้แจ้งย้ายให้เสร็จก่อนแล้วค่อยขอสำเนาใหม่",
      "เก็บไฟล์สแกนความละเอียดสูงไว้ เพราะหลายหน่วยงานรับยื่นออนไลน์",
    ],
    tipsEn: [
      "If the receiver asks for proof of address, ask whether a page copy or a registrar's certificate is expected",
      "Keep issue dates across the file close together to avoid questions about currency",
      "If you move during the process, complete the change of address first, then order fresh copies",
      "Keep high-resolution scans; many authorities now accept online submissions",
    ],
    pitfallsTh: [
      "ส่งเฉพาะหน้าปกทะเบียนบ้านโดยไม่มีหน้ารายการบุคคล",
      "แปล ท.ร.13 ด้วยแบบฟอร์มของ ท.ร.14 ทำให้หัวเรื่องผิดประเภทเอกสาร",
      "ที่อยู่ในคำแปลไม่ตรงกับเอกสารอื่นในชุด เช่น ใบรับรองเงินเดือนหรือสัญญาเช่า",
      "ใช้สำเนาเก่าเกินกำหนดของปลายทาง ทำให้ต้องคัดใหม่ทั้งชุด",
    ],
    pitfallsEn: [
      "Sending only the cover page without the page listing the persons",
      "Translating a Thor Ror 13 on a Thor Ror 14 template, mislabelling the document type",
      "An address that disagrees with other documents in the file, such as a salary letter or lease",
      "Using a copy older than the receiver allows, forcing the whole set to be reissued",
    ],
    faqsTh: [
      {
        q: "ต้องแปลทะเบียนบ้านทั้งเล่มหรือไม่",
        a: "ส่วนใหญ่ไม่ต้อง ปลายทางมักขอเฉพาะหน้าที่แสดงที่ตั้งบ้านและหน้าที่มีรายการของผู้ยื่น แต่ควรยืนยันกับหน่วยงานที่รับเรื่องก่อน เพราะบางแห่งขอครบทุกหน้าที่มีรายการสมาชิกในครอบครัว",
      },
      {
        q: "คนต่างชาติมีชื่อในทะเบียนบ้านได้หรือไม่",
        a: "ได้ โดยบันทึกในทะเบียนบ้าน ท.ร.13 สำหรับผู้ที่เข้าเมืองโดยชอบด้วยกฎหมายแต่ยังไม่ได้สัญชาติไทย เงื่อนไขการเพิ่มชื่อเป็นอำนาจของนายทะเบียนตามพระราชบัญญัติการทะเบียนราษฎร พ.ศ. 2534",
      },
      {
        q: "ทะเบียนบ้านใช้เป็นหลักฐานที่อยู่ในต่างประเทศได้ไหม",
        a: "ใช้ได้เมื่อผ่านการแปลและรับรองคำแปลตามที่ปลายทางกำหนด บางหน่วยงานต้องการหนังสือรับรองรายการทะเบียนราษฎรที่นายทะเบียนออกให้แทน เพราะระบุที่อยู่ปัจจุบันในรูปแบบหนังสือราชการ",
      },
      {
        q: "สำเนาทะเบียนบ้านที่ถ่ายเองใช้ยื่นนิติกรณ์ได้ไหม",
        a: "ไม่ได้ ต้องเป็นสำเนาที่นายทะเบียนลงนามรับรอง หรือใช้ฉบับเจ้าบ้านประกอบ เพราะขั้นตอนรับรองคำแปลอ้างอิงกับเอกสารที่หน่วยงานรัฐรับรองแล้ว",
      },
      {
        q: "ย้ายที่อยู่แล้วต้องทำอย่างไรกับเอกสารที่แปลไว้",
        a: "หากที่อยู่เปลี่ยนหลังแปลเสร็จ ปลายทางอาจถือว่าเอกสารไม่เป็นปัจจุบัน แนะนำให้แจ้งย้ายให้เรียบร้อยก่อน แล้วคัดสำเนาใหม่และแปลใหม่ทั้งชุด",
      },
      {
        q: "ขอคำแนะนำเฉพาะกรณีได้อย่างไร",
        a: "ส่งภาพถ่ายหน้าเอกสารที่ต้องใช้มาทาง LINE @nycli โทร +66-83-249-4999 หรืออีเมล nycli@ilc.ltd ทีมงานจะแจ้งว่าต้องแปลกี่หน้า ต้องรับรองขั้นตอนใดบ้าง และกรอบเวลาที่เป็นไปได้จริง",
      },
    ],
    faqsEn: [
      {
        q: "Does the whole book have to be translated?",
        a: "Usually not. Receivers typically want the address page and the page listing the applicant, but confirm first — some ask for every page listing family members.",
      },
      {
        q: "Can a foreign national be listed in a house registration?",
        a: "Yes, in a Thor Ror 13 book for persons lawfully resident without Thai nationality. Adding a name is the registrar's decision under the Civil Registration Act B.E. 2534.",
      },
      {
        q: "Is a house registration accepted as proof of address abroad?",
        a: "A house registration is accepted as proof of address abroad once it is translated and legalised according to the receiving authority's requirements. However, some authorities prefer a registrar-issued certificate of particulars because it states the current address in official letter form.",
      },
      {
        q: "Can a self-made photocopy be legalised?",
        a: "No. The copy must be certified by the registrar, or the householder's book presented, because legalisation builds on a document the state has already certified.",
      },
      {
        q: "What if we move after the translation is done?",
        a: "The receiver may treat the file as out of date. Complete the change of address first, then order fresh copies and a new translation.",
      },
      {
        q: "How do we get advice on our specific case?",
        a: "Send photos of the pages you need via LINE @nycli, call +66-83-249-4999 or email nycli@ilc.ltd. We will confirm how many pages to translate, which certifications apply and a realistic timeline.",
      },
    ],
  },
  {
    slug: "id-card",
    titleTh: "บัตรประจำตัวประชาชนและหนังสือรับรองรายการบัตร",
    titleEn: "National ID card and card-particulars certificate",
    summaryTh:
      "บัตรประจำตัวประชาชนออกตามพระราชบัญญัติบัตรประจำตัวประชาชน พ.ศ. 2526 ใช้ยืนยันตัวบุคคลและเลขประจำตัว 13 หลัก เมื่อใช้ต่างประเทศมักต้องแปลและรับรอง หรือขอหนังสือรับรองรายการบัตรจากสำนักทะเบียน",
    summaryEn:
      "The Thai national ID card is issued under the National Identity Card Act B.E. 2526 and evidences identity and the 13-digit ID number. For overseas use it is translated and legalised, or replaced by a registrar-issued certificate of card particulars.",
    bodyTh: [
      "บัตรประจำตัวประชาชนแสดงชื่อตัว ชื่อสกุล วันเดือนปีเกิด ที่อยู่ตามทะเบียนบ้าน และเลขประจำตัวประชาชน 13 หลัก ด้านหลังบัตรมีรหัสควบคุมการผลิตซึ่งบางหน่วยงานปลายทางขอให้แปลด้วย จึงควรสแกนทั้งสองด้านก่อนสั่งแปล",
      "เมื่อปลายทางต้องการเอกสารที่รัฐออกให้เป็นแผ่นกระดาษ ไม่ใช่บัตรพลาสติก สามารถขอหนังสือรับรองรายการบัตรประจำตัวประชาชนจากสำนักทะเบียนได้ เอกสารนี้สะดวกกว่าในงานนิติกรณ์ เพราะรับรองสำเนาและแปลได้ตรงไปตรงมา",
      "ชื่อภาษาอังกฤษบนบัตรกับชื่อในหนังสือเดินทางอาจสะกดต่างกันได้ในบางกรณี โดยเฉพาะผู้ที่เคยเปลี่ยนชื่อหรือถอดอักษรคนละระบบ กรณีนี้ต้องเลือกยึดตัวสะกดตามหนังสือเดินทางในคำแปล และแนบหลักฐานการเปลี่ยนชื่อเมื่อจำเป็น เพื่อไม่ให้ปลายทางมองว่าเป็นคนละบุคคล",
      "งานที่ใช้บ่อยคือการมอบอำนาจให้ดำเนินการแทนในต่างประเทศ การเปิดบัญชีหรือลงนามสัญญาข้ามประเทศ และการยื่นเอกสารต่อหน่วยงานที่ขอหลักฐานยืนยันตัวบุคคลของผู้มอบอำนาจ ซึ่งมักขอให้รับรองสำเนาถูกต้องโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารควบคู่ไปด้วย",
    ],
    bodyEn: [
      "The card shows the given name, surname, date of birth, registered address and the 13-digit identification number; the reverse carries a production control code that some receivers also want translated, so scan both sides before ordering.",
      "Where the receiver wants a paper document issued by the state rather than a plastic card, request a certificate of card particulars from the registration office. It is easier to handle in legalisation because it can be copy-certified and translated directly.",
      "The romanised name on the card and the name in the passport can differ, especially after a name change or where a different transliteration system was used. The translation should follow the passport spelling, with the change-of-name certificate attached where needed, so the receiver does not treat you as two different people.",
      "Typical uses are powers of attorney executed for use abroad, cross-border account opening and contract signing, and any filing where the receiver wants proof of the principal's identity — usually alongside a copy certified by a Notarial Services Attorney.",
    ],
    documentsTh: [
      "บัตรประจำตัวประชาชนที่ยังไม่หมดอายุ (สแกนหรือถ่ายสำเนาทั้งสองด้าน)",
      "หนังสือรับรองรายการบัตรประจำตัวประชาชนจากสำนักทะเบียน กรณีปลายทางขอเอกสารกระดาษ",
      "หนังสือเดินทางฉบับที่จะใช้ยื่น เพื่อยึดตัวสะกดชื่อในคำแปล",
      "หนังสือสำคัญการเปลี่ยนชื่อหรือชื่อสกุล หากตัวสะกดไม่ตรงกัน",
      "หนังสือมอบอำนาจ กรณีให้ผู้อื่นดำเนินการแทน",
    ],
    documentsEn: [
      "A valid national ID card, scanned or copied on both sides",
      "A certificate of card particulars from the registration office where a paper document is required",
      "The passport to be filed, to anchor the spelling of names",
      "Change-of-name certificates where spellings differ",
      "A power of attorney if a representative will act for you",
    ],
    chainTh: [
      "เตรียมสำเนาบัตรทั้งสองด้าน หรือขอหนังสือรับรองรายการบัตรจากสำนักทะเบียน",
      "แปลรายการบนบัตรให้ครบ รวมถึงวันออกบัตรและวันหมดอายุ",
      "รับรองสำเนาถูกต้องโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร เมื่อปลายทางกำหนด",
      "ยื่นรับรองคำแปลที่กรมการกงสุล",
      "รับรองต่อสถานเอกอัครราชทูตของประเทศปลายทาง เมื่อประเทศนั้นกำหนด",
    ],
    chainEn: [
      "Prepare copies of both sides, or obtain a certificate of card particulars from the registrar",
      "Translate every field, including the issue and expiry dates",
      "Have the copy certified by a Notarial Services Attorney where the receiver requires it",
      "File the translation for legalisation at the Department of Consular Affairs",
      "Endorse at the destination country's embassy where that country requires it",
    ],
    checklistTh: [
      "บัตรยังไม่หมดอายุ ณ วันที่ยื่นต่อปลายทาง",
      "สแกนครบทั้งด้านหน้าและด้านหลัง อ่านตัวเลขได้ชัดเจน",
      "ตัวสะกดชื่อในคำแปลยึดตามหนังสือเดินทาง",
      "เลขประจำตัว 13 หลักในคำแปลตรงกับบัตรทุกหลัก",
      "แนบหลักฐานการเปลี่ยนชื่อเมื่อชื่อในบัตรกับหนังสือเดินทางไม่ตรงกัน",
    ],
    checklistEn: [
      "The card is still valid on the date of filing",
      "Both sides are scanned and all digits are legible",
      "Name spellings in the translation follow the passport",
      "The 13-digit number is reproduced digit for digit",
      "Change-of-name evidence is attached where card and passport differ",
    ],
    tipsTh: [
      "ถ้าบัตรใกล้หมดอายุ ให้ทำบัตรใหม่ก่อนเริ่มงานแปลและนิติกรณ์",
      "เลือกใช้หนังสือรับรองรายการบัตรเมื่อปลายทางไม่รับสำเนาบัตรพลาสติก",
      "ปิดข้อมูลที่ไม่จำเป็นไม่ได้ในงานนิติกรณ์ เพราะคำแปลต้องตรงกับต้นฉบับทุกรายการ",
      "เก็บสำเนาที่รับรองแล้วไว้หลายชุด เพราะงานมอบอำนาจมักใช้แนบหลายแฟ้ม",
    ],
    tipsEn: [
      "Renew a card close to expiry before starting translation and legalisation",
      "Use the certificate of card particulars when the receiver will not accept a card copy",
      "Do not redact fields — the translation must mirror the original completely",
      "Keep several certified copies; power-of-attorney files often need one per matter",
    ],
    pitfallsTh: [
      "ส่งสำเนาด้านหน้าอย่างเดียว ทำให้ขาดรายการด้านหลังที่ปลายทางขอ",
      "ใช้บัตรที่หมดอายุแล้วเป็นหลักฐานยืนยันตัวบุคคล",
      "คำแปลใช้ตัวสะกดตามบัตรแต่หนังสือเดินทางสะกดอีกแบบ",
      "ถ่ายสำเนาไม่ชัดจนเลขประจำตัวอ่านไม่ออก ทำให้ต้องยื่นใหม่",
    ],
    pitfallsEn: [
      "Sending only the front side and missing the reverse fields the receiver wants",
      "Relying on an expired card as identity evidence",
      "Using the card spelling in the translation while the passport spells the name differently",
      "A blurred copy where the ID number cannot be read, forcing a refiling",
    ],
    faqsTh: [
      {
        q: "สำเนาบัตรประชาชนต้องรับรองโดยใคร",
        a: "เจ้าของบัตรลงนามรับรองสำเนาถูกต้องได้ แต่หากปลายทางต้องการการรับรองที่ตรวจสอบตัวบุคคล มักขอให้ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารรับรองสำเนาและลายมือชื่อให้",
      },
      {
        q: "บัตรหมดอายุใช้ยื่นได้ไหม",
        a: "โดยทั่วไปไม่ควรใช้ เพราะปลายทางถือว่าไม่เป็นปัจจุบัน แนะนำให้ทำบัตรใหม่ที่สำนักทะเบียนก่อน แล้วจึงเริ่มขั้นตอนแปลและนิติกรณ์",
      },
      {
        q: "หนังสือรับรองรายการบัตรคืออะไร",
        a: "เป็นเอกสารที่สำนักทะเบียนออกให้เพื่อรับรองรายการที่ปรากฏในบัตรประจำตัวประชาชน เหมาะกับกรณีที่ปลายทางต้องการเอกสารกระดาษที่มีลายมือชื่อเจ้าหน้าที่และตราประทับ",
      },
      {
        q: "ชื่อบนบัตรกับหนังสือเดินทางสะกดไม่ตรงกันจะมีปัญหาหรือไม่",
        a: "อาจมีปัญหาได้ วิธีที่ปลอดภัยคือยึดตัวสะกดตามหนังสือเดินทางในคำแปล และแนบหนังสือสำคัญการเปลี่ยนชื่อหรือเอกสารที่แสดงความเชื่อมโยงของทั้งสองชื่อ",
      },
      {
        q: "ต้องแปลด้านหลังบัตรด้วยหรือไม่",
        a: "ขึ้นกับปลายทาง บางแห่งขอเฉพาะด้านหน้า บางแห่งขอทั้งสองด้านรวมรหัสควบคุมการผลิต แนะนำให้สแกนไว้ทั้งสองด้านตั้งแต่ต้นเพื่อไม่ต้องกลับมาทำซ้ำ",
      },
      {
        q: "ให้เราช่วยตรวจก่อนยื่นได้ไหม",
        a: "ได้ครับ ส่งไฟล์สแกนมาทาง LINE @nycli โทร +66-83-249-4999 หรืออีเมล nycli@ilc.ltd ทีมงานจะแจ้งว่าต้องใช้รูปแบบเอกสารใดและต้องรับรองชั้นใดบ้าง",
      },
    ],
    faqsEn: [
      {
        q: "Who should certify a copy of the ID card?",
        a: "The holder can certify their own copy, but where the receiver wants identity verified it usually asks a Notarial Services Attorney to certify the copy and the signature.",
      },
      {
        q: "Can an expired card be filed?",
        a: "Generally not — receivers treat it as out of date. Renew the card at the registration office first, then start translation and legalisation.",
      },
      {
        q: "What is a certificate of card particulars?",
        a: "A registration-office document confirming the particulars shown on the ID card. It suits receivers that want a paper record with an official signature and seal.",
      },
      {
        q: "Is a spelling mismatch between card and passport a problem?",
        a: "A spelling mismatch between an ID card and a passport can cause official document processing issues. To resolve this, follow the exact passport spelling in the translation and attach a change-of-name certificate or other official evidence linking the two spellings together.",
      },
      {
        q: "Does the reverse of the card need translating?",
        a: "Translating the reverse side of an ID card depends on the requirements of the receiving authority. While some receivers accept only the front side, others require both sides including the control code. Scanning both sides up front prevents having to repeat the work later.",
      },
      {
        q: "Can you review our documents before filing?",
        a: "Yes. Send the scans via LINE @nycli, +66-83-249-4999 or nycli@ilc.ltd and we will confirm the format and the certification layers required.",
      },
    ],
  },
  {
    slug: "name-change",
    titleTh: "หนังสือสำคัญการเปลี่ยนชื่อและชื่อสกุล",
    titleEn: "Change-of-name and surname certificates",
    summaryTh:
      "การเปลี่ยนชื่อตัวและชื่อสกุลดำเนินการตามพระราชบัญญัติชื่อบุคคล พ.ศ. 2505 โดยนายทะเบียนออกหนังสือสำคัญเป็นหลักฐาน เอกสารนี้ใช้เชื่อมชื่อเดิมกับชื่อใหม่เมื่อยื่นเอกสารต่อหน่วยงานต่างประเทศ",
    summaryEn:
      "Changing a given name or surname is done under the Person Name Act B.E. 2505, and the registrar issues a certificate as evidence. That certificate links the old and new names when documents are filed abroad.",
    bodyTh: [
      "เมื่อบุคคลเปลี่ยนชื่อตัวหรือชื่อสกุล นายทะเบียนจะออกหนังสือสำคัญแสดงการเปลี่ยนชื่อตัว (แบบ ช.3) หรือหนังสือสำคัญแสดงการเปลี่ยนชื่อสกุล (แบบ ช.5) แล้วแต่กรณี เอกสารนี้เป็นหลักฐานเดียวที่แสดงว่าบุคคลตามชื่อเดิมกับชื่อใหม่คือคนเดียวกัน จึงสำคัญมากในแฟ้มเอกสารข้ามประเทศ",
      "ปัญหาที่พบบ่อยคือเอกสารเก่า เช่น สูติบัตร ปริญญาบัตร ใบรับรองการทำงาน หรือทะเบียนสมรส ยังใช้ชื่อเดิม ขณะที่หนังสือเดินทางและเอกสารปัจจุบันใช้ชื่อใหม่ หากไม่แนบหนังสือสำคัญการเปลี่ยนชื่อพร้อมคำแปล หน่วยงานปลายทางอาจถือว่าเอกสารไม่สอดคล้องกันและปฏิเสธแฟ้ม",
      "การสมรสหรือการหย่าอาจทำให้ชื่อสกุลเปลี่ยนตามข้อตกลงการใช้ชื่อสกุล กรณีนี้หลักฐานที่ใช้คือทะเบียนสมรสหรือทะเบียนหย่าและบันทึกข้อตกลงที่เกี่ยวข้อง ควบคู่กับหนังสือสำคัญที่นายทะเบียนออกให้เมื่อมีการเปลี่ยนรายการ",
      "ในงานนิติกรณ์ควรจัดชุดเอกสารตามลำดับเวลา คือเอกสารชื่อเดิม หนังสือสำคัญการเปลี่ยนชื่อ และเอกสารชื่อปัจจุบัน พร้อมคำแปลทั้งชุด เพื่อให้เจ้าหน้าที่ปลายทางเห็นเส้นทางการเปลี่ยนชื่อได้ในครั้งเดียว",
    ],
    bodyEn: [
      "When a person changes a given name or surname, the registrar issues a change-of-given-name certificate (form Chor 3) or a change-of-surname certificate (form Chor 5). It is the only document proving that the old and new names belong to the same person, which makes it critical in cross-border files.",
      "The common problem is older documents — birth certificate, degree, employment letter, marriage record — still bearing the former name while the passport and current papers show the new one. Without the change-of-name certificate and its translation, the receiver may treat the file as inconsistent and refuse it.",
      "Marriage or divorce may also change a surname under the spouses' agreement on surname use. There the evidence is the marriage or divorce registration and the related agreement record, together with any certificate the registrar issues when the entry changes.",
      "Assemble the legalisation set in chronological order: former-name documents, the change-of-name certificate, then current-name documents, all translated, so the receiving officer can follow the whole naming history at once.",
    ],
    documentsTh: [
      "หนังสือสำคัญแสดงการเปลี่ยนชื่อตัว (ช.3) หรือเปลี่ยนชื่อสกุล (ช.5) ฉบับจริงหรือสำเนารับรอง",
      "บัตรประจำตัวประชาชนและทะเบียนบ้านที่แสดงชื่อปัจจุบัน",
      "เอกสารที่ยังใช้ชื่อเดิมซึ่งจะยื่นควบคู่กัน",
      "ทะเบียนสมรสหรือทะเบียนหย่า กรณีชื่อสกุลเปลี่ยนจากการสมรสหรือหย่า",
      "หนังสือเดินทางฉบับที่จะใช้ยื่น",
    ],
    documentsEn: [
      "The original or a certified copy of the Chor 3 or Chor 5 certificate",
      "ID card and house registration showing the current name",
      "The former-name documents that will be filed alongside",
      "Marriage or divorce registration where the surname changed on marriage or divorce",
      "The passport that will be filed",
    ],
    chainTh: [
      "รวบรวมหนังสือสำคัญการเปลี่ยนชื่อทุกครั้งที่เคยเปลี่ยน ไม่ใช่เฉพาะครั้งล่าสุด",
      "แปลหนังสือสำคัญและเอกสารชื่อเดิมให้ใช้ตัวสะกดสอดคล้องกันทั้งชุด",
      "รับรองสำเนาโดยทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร เมื่อปลายทางกำหนด",
      "ยื่นรับรองคำแปลที่กรมการกงสุล",
      "รับรองต่อสถานเอกอัครราชทูตของประเทศปลายทาง เมื่อประเทศนั้นกำหนด",
    ],
    chainEn: [
      "Collect every change-of-name certificate ever issued, not only the most recent",
      "Translate the certificates and the former-name documents with consistent spellings throughout",
      "Add attorney copy certification where the receiver requires it",
      "File the translations for legalisation at the Department of Consular Affairs",
      "Endorse at the destination country's embassy where that country requires it",
    ],
    checklistTh: [
      "มีหนังสือสำคัญครบทุกครั้งที่เปลี่ยนชื่อ เรียงตามลำดับเวลา",
      "ตัวสะกดชื่อใหม่ในคำแปลตรงกับหนังสือเดินทางปัจจุบัน",
      "ตัวสะกดชื่อเดิมในคำแปลตรงกับเอกสารเก่าที่ยื่นควบคู่",
      "เลขประจำตัวประชาชนในทุกฉบับตรงกัน ยืนยันว่าเป็นบุคคลเดียวกัน",
      "สำเนาทุกฉบับมีลายมือชื่อผู้รับรองครบถ้วน",
    ],
    checklistEn: [
      "Every certificate from every change is present, in chronological order",
      "The new-name spelling in the translation matches the current passport",
      "The former-name spelling matches the older documents filed with it",
      "The national ID number is identical across all documents, proving one person",
      "Every copy carries a complete certification signature",
    ],
    tipsTh: [
      "ทำสารบัญหน้าเดียวอธิบายลำดับชื่อเดิมและชื่อใหม่ ช่วยให้เจ้าหน้าที่ปลายทางอ่านแฟ้มได้เร็ว",
      "หากเปลี่ยนชื่อหลายครั้ง ให้แปลทุกฉบับ ไม่ควรข้ามฉบับกลาง",
      "ตรวจสอบกับปลายทางว่าต้องการหนังสือสำคัญฉบับจริงหรือสำเนารับรอง",
      "เก็บชุดคำแปลที่รับรองแล้วเป็นไฟล์ดิจิทัล เพื่อใช้ซ้ำในงานอื่น",
    ],
    tipsEn: [
      "Add a one-page index of the naming sequence so the receiving officer can follow the file quickly",
      "If the name changed several times, translate every certificate — do not skip intermediate ones",
      "Confirm whether the receiver wants originals or certified copies",
      "Keep a digital set of the legalised translations for reuse in later matters",
    ],
    pitfallsTh: [
      "ยื่นเอกสารชื่อเดิมโดยไม่แนบหนังสือสำคัญการเปลี่ยนชื่อ",
      "แปลหนังสือสำคัญด้วยตัวสะกดที่ต่างจากหนังสือเดินทาง",
      "แปลเฉพาะการเปลี่ยนชื่อครั้งล่าสุด ทำให้เส้นทางชื่อขาดช่วง",
      "ใช้ทะเบียนสมรสอย่างเดียวอธิบายการเปลี่ยนชื่อสกุลทั้งที่ปลายทางขอหนังสือสำคัญจากนายทะเบียน",
    ],
    pitfallsEn: [
      "Filing former-name documents without the change-of-name certificate",
      "Translating the certificate with a spelling that differs from the passport",
      "Translating only the latest change, leaving a gap in the naming history",
      "Relying on the marriage record alone when the receiver asks for the registrar's certificate",
    ],
    faqsTh: [
      {
        q: "เปลี่ยนชื่อแล้วต้องแก้เอกสารเก่าทั้งหมดหรือไม่",
        a: "ไม่จำเป็นต้องแก้เอกสารเก่า เพราะเอกสารเหล่านั้นออกไว้ตามข้อเท็จจริง ณ เวลานั้น แต่ต้องแนบหนังสือสำคัญการเปลี่ยนชื่อพร้อมคำแปลเพื่อยืนยันว่าเป็นบุคคลเดียวกัน",
      },
      {
        q: "เปลี่ยนชื่อหลายครั้งต้องแปลทุกฉบับไหม",
        a: "ควรแปลทุกฉบับตามลำดับเวลา เพราะหน่วยงานปลายทางต้องเห็นเส้นทางชื่อที่ต่อเนื่องกัน หากขาดฉบับกลางอาจถือว่าหลักฐานไม่เชื่อมโยงกัน",
      },
      {
        q: "ชื่อสกุลเปลี่ยนจากการสมรสใช้เอกสารใด",
        a: "ใช้ทะเบียนสมรสและบันทึกข้อตกลงการใช้ชื่อสกุลเป็นหลักฐานหลัก และแนบหนังสือสำคัญที่นายทะเบียนออกให้เมื่อมีการเปลี่ยนรายการ ถ้าปลายทางกำหนด",
      },
      {
        q: "หนังสือสำคัญหายต้องทำอย่างไร",
        a: "ติดต่อสำนักทะเบียนที่ออกเอกสารเพื่อขอคัดและรับรองสำเนาจากทะเบียน หรือขอออกใบแทนตามที่นายทะเบียนกำหนด เงื่อนไขและเอกสารประกอบเป็นดุลพินิจของนายทะเบียนตามระเบียบ",
      },
      {
        q: "คำแปลต้องใช้ตัวสะกดตามบัตรหรือหนังสือเดินทาง",
        a: "ให้ยึดตัวสะกดตามหนังสือเดินทางฉบับที่จะใช้ยื่นเป็นหลัก เพราะหน่วยงานปลายทางเทียบกับหนังสือเดินทางเป็นอันดับแรก แล้วอธิบายชื่อเดิมด้วยหนังสือสำคัญการเปลี่ยนชื่อ",
      },
      {
        q: "ต้องการให้ช่วยจัดชุดเอกสารทำอย่างไร",
        a: "ส่งรายการเอกสารและภาพสแกนมาทาง LINE @nycli โทร +66-83-249-4999 หรืออีเมล nycli@ilc.ltd ทีมงานจะจัดลำดับแฟ้ม แจ้งฉบับที่ต้องแปล และขั้นตอนรับรองที่จำเป็น",
      },
    ],
    faqsEn: [
      {
        q: "Must all older documents be amended after a name change?",
        a: "No. They record the facts as at their issue date. Attach the change-of-name certificate with its translation to prove the same person is involved.",
      },
      {
        q: "If the name changed several times, must every certificate be translated?",
        a: "Translating certificates for multiple name changes must be completed in chronological order for foreign authorities. Receiving authorities require an unbroken naming history to verify identity. Omitting an intermediate certificate breaks the required chain of legal evidence.",
      },
      {
        q: "Which document proves a surname change on marriage?",
        a: "The marriage registration and the agreement on surname use, plus any registrar certificate issued when the entry changed, if the receiver asks for it.",
      },
      {
        q: "What if the certificate is lost?",
        a: "Ask the issuing registration office for a certified extract from the register, or for a replacement as the registrar's regulations allow; the requirements are the registrar's to set.",
      },
      {
        q: "Should the translation follow the ID card or the passport spelling?",
        a: "Follow the passport that will be filed — receivers match against it first — and explain the former name through the change-of-name certificate.",
      },
      {
        q: "Can you assemble the file for us?",
        a: "Send the list and scans via LINE @nycli, +66-83-249-4999 or nycli@ilc.ltd. We will order the file, identify what needs translating and set out the certification steps.",
      },
    ],
  },
  {
    slug: "death-certificate",
    titleTh: "มรณบัตรและเอกสารแจ้งตาย",
    titleEn: "Death certificate and death notification records",
    summaryTh:
      "มรณบัตร (ท.ร.4) ออกโดยนายทะเบียนหลังการแจ้งตายตามพระราชบัญญัติการทะเบียนราษฎร พ.ศ. 2534 ใช้ในงานมรดก ประกันชีวิต การจัดการศพ และงานทะเบียนในต่างประเทศ",
    summaryEn:
      "A death certificate (Thor Ror 4) is issued by the registrar after a death is notified under the Civil Registration Act B.E. 2534. It supports estate matters, life-insurance claims, repatriation and civil filings abroad.",
    bodyTh: [
      "เมื่อมีผู้เสียชีวิต ผู้มีหน้าที่แจ้งตายต้องแจ้งต่อนายทะเบียนภายในกำหนดตามพระราชบัญญัติการทะเบียนราษฎร พ.ศ. 2534 หากเสียชีวิตในสถานพยาบาล จะมีหนังสือรับรองการตาย (ท.ร.4/1) จากสถานพยาบาลเป็นหลักฐานประกอบ นายทะเบียนจึงออกมรณบัตรซึ่งเป็นเอกสารราชการที่หน่วยงานทั้งในและต่างประเทศยอมรับ",
      "กรณีการตายผิดธรรมชาติ กฎหมายกำหนดให้มีการชันสูตรพลิกศพก่อน ซึ่งอาจทำให้มีเอกสารเพิ่มเติมจากพนักงานสอบสวนหรือแพทย์ผู้ชันสูตร เอกสารเหล่านี้บางครั้งถูกร้องขอโดยบริษัทประกันหรือศาลต่างประเทศควบคู่กับมรณบัตร",
      "ในงานมรดกข้ามประเทศ ปลายทางมักขอมรณบัตรที่แปลและรับรองแล้ว พร้อมเอกสารแสดงความสัมพันธ์ เช่น ทะเบียนบ้าน ทะเบียนสมรส หรือสูติบัตรของทายาท ควรเตรียมทั้งชุดพร้อมกันเพื่อลดรอบการยื่น",
      "กรณีคนต่างชาติเสียชีวิตในประเทศไทย ครอบครัวมักต้องใช้มรณบัตรไทยที่แปลและรับรองแล้วยื่นต่อสถานทูตของประเทศนั้นเพื่อดำเนินการต่อ ทั้งการเคลื่อนย้ายศพและการจัดการทรัพย์สิน ขั้นตอนที่แน่นอนขึ้นกับกฎของสถานทูตแต่ละแห่ง จึงควรสอบถามสถานทูตโดยตรงควบคู่ไปด้วย",
    ],
    bodyEn: [
      "On a death, the person responsible must notify the registrar within the period set by the Civil Registration Act B.E. 2534. Where the death occurred in a healthcare facility, the facility's certificate of death (Thor Ror 4/1) supports the notification, and the registrar then issues the death certificate accepted by authorities at home and abroad.",
      "For an unnatural death the law requires an inquest first, which can generate further documents from the investigating officer or the examining physician. Insurers and foreign courts sometimes request those alongside the death certificate.",
      "In cross-border estate matters the receiver usually wants a translated and legalised death certificate together with proof of relationship — a house registration, marriage record or the heirs' birth certificates. Preparing the whole set at once reduces the number of filing rounds.",
      "When a foreign national dies in Thailand, the family generally files a translated and legalised Thai death certificate with that country's embassy for repatriation and estate steps. The exact procedure is the embassy's, so ask it directly in parallel.",
    ],
    documentsTh: [
      "มรณบัตรฉบับจริง หรือสำเนาที่นายทะเบียนลงนามรับรอง",
      "หนังสือรับรองการตายจากสถานพยาบาล (ถ้ามี)",
      "บัตรประจำตัวประชาชนหรือหนังสือเดินทางของผู้เสียชีวิต",
      "เอกสารแสดงความสัมพันธ์ของผู้ยื่น เช่น ทะเบียนบ้าน ทะเบียนสมรส หรือสูติบัตร",
      "หนังสือมอบอำนาจ กรณีให้ผู้อื่นดำเนินการแทน",
    ],
    documentsEn: [
      "The original death certificate, or a registrar-certified copy",
      "The healthcare facility's certificate of death, if any",
      "The deceased's ID card or passport",
      "Proof of the applicant's relationship: house registration, marriage record or birth certificate",
      "A power of attorney if a representative will act",
    ],
    chainTh: [
      "แจ้งตายและรับมรณบัตรจากสำนักทะเบียนในพื้นที่ที่เกิดเหตุ",
      "ขอสำเนารับรองเพิ่มตามจำนวนแฟ้มที่ต้องยื่น",
      "แปลมรณบัตรและเอกสารแสดงความสัมพันธ์เป็นภาษาที่ปลายทางกำหนด",
      "รับรองคำแปลที่กรมการกงสุล โดยรับรองสำเนาหรือลายมือชื่อโดยทนายความก่อนเมื่อปลายทางกำหนด",
      "รับรองต่อสถานเอกอัครราชทูตของประเทศปลายทาง เมื่อประเทศนั้นกำหนด",
    ],
    chainEn: [
      "Notify the death and collect the certificate from the local registration office",
      "Order additional certified copies for each file to be submitted",
      "Translate the certificate and the relationship documents into the required language",
      "Legalise the translations at the Department of Consular Affairs, with prior attorney certification where required",
      "Endorse at the destination country's embassy where that country requires it",
    ],
    checklistTh: [
      "ชื่อผู้เสียชีวิตในมรณบัตรตรงกับหนังสือเดินทางหรือบัตรประจำตัว",
      "วันที่และสถานที่ตายในคำแปลตรงกับต้นฉบับ",
      "จำนวนสำเนารับรองพอสำหรับทุกหน่วยงานที่ต้องยื่น",
      "เอกสารแสดงความสัมพันธ์ของผู้ยื่นครบและแปลพร้อมกัน",
      "สอบถามสถานทูตปลายทางถึงเอกสารเพิ่มเติมก่อนยื่นจริง",
    ],
    checklistEn: [
      "The deceased's name matches the passport or ID card",
      "The date and place of death in the translation match the original",
      "Enough certified copies for every authority in the file",
      "Relationship documents are complete and translated at the same time",
      "The destination embassy has been asked about any extra documents",
    ],
    tipsTh: [
      "ขอสำเนารับรองหลายชุดตั้งแต่ครั้งแรก เพราะแต่ละหน่วยงานมักเก็บเอกสารไว้",
      "แจ้งวัตถุประสงค์ปลายทางกับผู้แปลตั้งแต่ต้น เพื่อเลือกรูปแบบคำแปลให้เหมาะสม",
      "หากต้องเคลื่อนย้ายศพระหว่างประเทศ ให้ประสานสถานทูตและสายการบินคู่ขนานกับงานเอกสาร",
      "เก็บสำเนาดิจิทัลของทุกฉบับไว้ เพื่อใช้ในงานมรดกที่อาจใช้เวลานาน",
    ],
    tipsEn: [
      "Order several certified copies at the outset; each authority tends to keep one",
      "Tell the translator the destination purpose up front so the format fits",
      "For repatriation, coordinate with the embassy and the airline in parallel with the paperwork",
      "Keep digital copies of everything; estate matters can run for a long time",
    ],
    pitfallsTh: [
      "ใช้หนังสือรับรองการตายของโรงพยาบาลแทนมรณบัตรของนายทะเบียน",
      "ขอสำเนารับรองไม่พอ ต้องกลับไปคัดใหม่ระหว่างดำเนินเรื่อง",
      "แปลเฉพาะมรณบัตร แต่ไม่แปลเอกสารแสดงความสัมพันธ์ที่ปลายทางขอ",
      "ชื่อผู้เสียชีวิตในเอกสารไทยกับเอกสารต่างประเทศสะกดไม่ตรงกันโดยไม่มีเอกสารเชื่อมโยง",
    ],
    pitfallsEn: [
      "Using the hospital's certificate of death instead of the registrar's death certificate",
      "Ordering too few certified copies and having to return mid-process",
      "Translating only the certificate and omitting the relationship documents the receiver wants",
      "A spelling mismatch between Thai and foreign records with nothing to link them",
    ],
    faqsTh: [
      {
        q: "หนังสือรับรองการตายกับมรณบัตรต่างกันอย่างไร",
        a: "หนังสือรับรองการตายออกโดยสถานพยาบาลเพื่อใช้ประกอบการแจ้งตาย ส่วนมรณบัตรออกโดยนายทะเบียนตามพระราชบัญญัติการทะเบียนราษฎร พ.ศ. 2534 และเป็นเอกสารที่หน่วยงานปลายทางต้องการ",
      },
      {
        q: "ญาติที่อยู่ต่างประเทศจะดำเนินการได้อย่างไร",
        a: "ทำหนังสือมอบอำนาจให้ผู้แทนในไทยดำเนินการ โดยหนังสือมอบอำนาจที่ทำในต่างประเทศมักต้องผ่านการรับรองที่สถานเอกอัครราชทูตหรือสถานกงสุลไทยในประเทศนั้นก่อนนำมาใช้ในไทย",
      },
      {
        q: "มรณบัตรใช้ยื่นเรื่องมรดกในต่างประเทศได้เลยหรือไม่",
        a: "ต้องผ่านการแปลและรับรองตามที่ปลายทางกำหนดก่อน และมักต้องแนบเอกสารแสดงความสัมพันธ์ของทายาท เช่น ทะเบียนบ้าน ทะเบียนสมรส หรือสูติบัตร",
      },
      {
        q: "กรณีตายผิดธรรมชาติต้องใช้เอกสารเพิ่มหรือไม่",
        a: "กฎหมายกำหนดให้มีการชันสูตรพลิกศพ ซึ่งอาจมีเอกสารจากพนักงานสอบสวนหรือแพทย์เพิ่มเติม บริษัทประกันหรือศาลต่างประเทศอาจขอเอกสารเหล่านี้ควบคู่กับมรณบัตร",
      },
      {
        q: "คนต่างชาติเสียชีวิตในไทยต้องแจ้งใครบ้าง",
        a: "แจ้งนายทะเบียนในพื้นที่เพื่อออกมรณบัตร และแจ้งสถานเอกอัครราชทูตของประเทศผู้เสียชีวิตเพื่อดำเนินการตามกฎของประเทศนั้น ขั้นตอนที่แน่นอนขึ้นกับสถานทูตแต่ละแห่ง",
      },
      {
        q: "ให้ช่วยเดินเรื่องเอกสารได้ไหม",
        a: "เราช่วยในขั้นตอนแปล รับรองโดยทนายความ และนิติกรณ์ที่กรมการกงสุลและสถานทูต ติดต่อ LINE @nycli โทร +66-83-249-4999 หรืออีเมล nycli@ilc.ltd เพื่อให้ทีมงานประเมินกรณีของท่าน",
      },
    ],
    faqsEn: [
      {
        q: "How does the hospital certificate differ from the death certificate?",
        a: "The hospital issues a certificate of death to support the notification; the registrar issues the death certificate under the Civil Registration Act B.E. 2534, and that is what receivers want.",
      },
      {
        q: "How can relatives abroad handle this?",
        a: "By granting a power of attorney to a representative in Thailand. A power of attorney executed abroad usually needs certification at the Thai embassy or consulate before use in Thailand.",
      },
      {
        q: "Can the death certificate be filed abroad as is?",
        a: "A death certificate cannot be filed abroad as is without being translated and legalised as required by the receiving authority. Filing typically requires proof of the heirs' relationship, such as a house registration, marriage record, or birth certificate.",
      },
      {
        q: "Are extra documents needed for an unnatural death?",
        a: "The law requires an inquest, which can produce additional documents from the investigating officer or physician. Insurers and foreign courts may ask for these too.",
      },
      {
        q: "Who must be notified when a foreign national dies in Thailand?",
        a: "When a foreign national dies in Thailand, both the local registrar and the deceased's embassy must be notified. The local registrar is notified to obtain the official death certificate, while the embassy is notified to carry out its country's official procedures.",
      },
      {
        q: "Can you handle the paperwork?",
        a: "We handle translation, attorney certification and legalisation at the Department of Consular Affairs and embassies. Contact LINE @nycli, +66-83-249-4999 or nycli@ilc.ltd.",
      },
    ],
  },
  {
    slug: "status-certificates",
    titleTh: "หนังสือรับรองสถานภาพและรายการทะเบียนราษฎร",
    titleEn: "Status and civil-registration particulars certificates",
    summaryTh:
      "หนังสือรับรองที่นายทะเบียนออกให้ เช่น หนังสือรับรองความเป็นโสด หนังสือรับรองสถานภาพสมรส และหนังสือรับรองรายการทะเบียนราษฎร ใช้ยืนยันสถานะบุคคลต่อหน่วยงานต่างประเทศ",
    summaryEn:
      "Registrar-issued certificates — single status, marital status and certificates of civil-registration particulars — evidence a person's civil status to foreign authorities.",
    bodyTh: [
      "หนังสือรับรองความเป็นโสดหรือหนังสือรับรองสถานภาพสมรสออกโดยสำนักทะเบียนตามข้อมูลที่ปรากฏในฐานข้อมูลทะเบียนราษฎรและทะเบียนครอบครัว เอกสารนี้มักถูกขอในการจดทะเบียนสมรสในต่างประเทศ การยื่นวีซ่าคู่สมรส หรือการดำเนินคดีครอบครัวข้ามประเทศ",
      "หนังสือรับรองรายการทะเบียนราษฎรเป็นเอกสารที่นายทะเบียนออกให้เพื่อยืนยันรายการที่ปรากฏในทะเบียน เช่น ที่อยู่ วันเกิด หรือความสัมพันธ์ในครอบครัว เหมาะกับกรณีที่ปลายทางต้องการเอกสารรูปแบบหนังสือราชการมากกว่าสำเนาหน้าเล่มทะเบียนบ้าน",
      "หน่วยงานต่างประเทศส่วนใหญ่กำหนดอายุเอกสารประเภทหนังสือรับรองสถานะ เพราะสถานะบุคคลเปลี่ยนแปลงได้ตลอดเวลา ควรตรวจสอบเงื่อนไขอายุเอกสารกับปลายทางก่อนเริ่มขั้นตอนแปลและนิติกรณ์ เพื่อไม่ให้เอกสารหมดอายุระหว่างทาง",
      "การใช้เอกสารกลุ่มนี้ในต่างประเทศต้องผ่านลำดับเดียวกับเอกสารทะเบียนราษฎรอื่น คือแปล รับรองคำแปลที่กรมการกงสุล และรับรองที่สถานทูตปลายทางเมื่อประเทศนั้นกำหนด บางประเทศยังกำหนดแบบข้อความเฉพาะ จึงควรขอตัวอย่างจากปลายทางก่อนยื่นคำขอที่สำนักทะเบียน",
    ],
    bodyEn: [
      "A single-status or marital-status certificate is issued by the registration office from the civil and family registration databases. It is commonly required to marry abroad, for spouse-visa files, and in cross-border family proceedings.",
      "A certificate of civil-registration particulars confirms entries such as address, date of birth or family relationships. It suits receivers that want an official letter rather than a copy of a house registration page.",
      "Most foreign authorities set a validity window for status certificates because civil status can change at any time. Check that window before starting translation and legalisation so the document does not expire in transit.",
      "These documents follow the same route as other civil records: translation, legalisation at the Department of Consular Affairs, then embassy endorsement where the destination country requires it. Some countries prescribe specific wording, so obtain a sample from the receiver before applying at the registration office.",
    ],
    documentsTh: [
      "หนังสือรับรองที่นายทะเบียนออกให้ ฉบับจริงพร้อมลายมือชื่อและตราประทับ",
      "บัตรประจำตัวประชาชนและทะเบียนบ้านของผู้ขอ",
      "หนังสือเดินทางฉบับที่จะใช้ยื่น",
      "ทะเบียนสมรสหรือทะเบียนหย่า กรณีเคยสมรสมาก่อน",
      "ตัวอย่างข้อความหรือแบบฟอร์มที่หน่วยงานปลายทางกำหนด (ถ้ามี)",
    ],
    documentsEn: [
      "The registrar's original certificate with signature and seal",
      "ID card and house registration of the applicant",
      "The passport that will be filed",
      "Marriage or divorce registration where there was a prior marriage",
      "Any wording or form template prescribed by the receiver",
    ],
    chainTh: [
      "สอบถามข้อความและอายุเอกสารที่ปลายทางกำหนดก่อนยื่นคำขอ",
      "ยื่นคำขอหนังสือรับรองที่สำนักทะเบียนที่มีเขตอำนาจ",
      "แปลเป็นภาษาที่ปลายทางกำหนด โดยคงข้อความรับรองให้ครบ",
      "รับรองคำแปลที่กรมการกงสุล พร้อมรับรองโดยทนายความก่อนเมื่อปลายทางกำหนด",
      "รับรองต่อสถานเอกอัครราชทูตของประเทศปลายทาง เมื่อประเทศนั้นกำหนด",
    ],
    chainEn: [
      "Ask the receiver about required wording and validity before applying",
      "Apply for the certificate at the registration office with jurisdiction",
      "Translate into the required language, keeping the attesting wording intact",
      "Legalise at the Department of Consular Affairs, with prior attorney certification where required",
      "Endorse at the destination country's embassy where that country requires it",
    ],
    checklistTh: [
      "ข้อความในหนังสือรับรองตรงกับที่ปลายทางกำหนด",
      "วันที่ออกเอกสารยังอยู่ในกรอบอายุที่ปลายทางรับ",
      "ชื่อและเลขประจำตัวตรงกับหนังสือเดินทางและเอกสารอื่นในชุด",
      "หากเคยสมรสหรือหย่า ได้แนบทะเบียนที่เกี่ยวข้องพร้อมคำแปล",
      "คำแปลคงข้อความรับรองและตำแหน่งผู้ลงนามไว้ครบ",
    ],
    checklistEn: [
      "The wording matches what the receiver prescribed",
      "The issue date is still inside the receiver's validity window",
      "Name and ID number agree with the passport and the rest of the file",
      "Prior marriage or divorce records are attached with translations",
      "The translation preserves the attesting wording and the signatory's title",
    ],
    tipsTh: [
      "วางแผนลำดับเวลาให้เอกสารถึงปลายทางก่อนหมดอายุ โดยเผื่อเวลาขั้นตอนนิติกรณ์",
      "ขอตัวอย่างเอกสารที่เคยผ่านจากปลายทาง ช่วยลดการแก้ไขข้อความ",
      "หากต้องใช้หลายประเทศ ให้ขอหนังสือรับรองหลายฉบับพร้อมกัน",
      "ตรวจสอบว่าปลายทางรับคำแปลภาษาอังกฤษหรือต้องแปลเป็นภาษาราชการของประเทศนั้น",
    ],
    tipsEn: [
      "Plan the timeline so the file lands before expiry, allowing for legalisation time",
      "Ask the receiver for a previously accepted sample to avoid rewording",
      "If several countries are involved, request multiple originals at once",
      "Confirm whether English is accepted or the local official language is required",
    ],
    pitfallsTh: [
      "เอกสารหมดอายุระหว่างรอขั้นตอนนิติกรณ์",
      "ข้อความในหนังสือรับรองไม่ตรงกับที่ปลายทางกำหนด ทำให้ต้องยื่นคำขอใหม่",
      "แปลตัดข้อความรับรองหรือชื่อตำแหน่งผู้ลงนามออก",
      "ไม่แนบทะเบียนหย่าในกรณีที่ปลายทางต้องการหลักฐานสิ้นสุดการสมรสก่อนหน้า",
    ],
    pitfallsEn: [
      "The certificate expiring while legalisation is still under way",
      "Wording that does not match the receiver's requirement, forcing a fresh application",
      "A translation that drops the attesting wording or the signatory's title",
      "Omitting the divorce record where the receiver needs proof the prior marriage ended",
    ],
    faqsTh: [
      {
        q: "หนังสือรับรองความเป็นโสดขอได้ที่ไหน",
        a: "ยื่นคำขอที่สำนักทะเบียนอำเภอหรือสำนักทะเบียนท้องถิ่นที่มีเขตอำนาจ นายทะเบียนจะออกให้ตามข้อมูลที่ปรากฏในฐานข้อมูลทะเบียนราษฎรและทะเบียนครอบครัว",
      },
      {
        q: "หนังสือรับรองสถานะมีอายุเท่าไร",
        a: "ตัวเอกสารไม่ได้กำหนดวันหมดอายุในตัวเอง แต่หน่วยงานปลายทางส่วนใหญ่กำหนดว่าต้องออกใหม่ภายในระยะเวลาที่กำหนด จึงต้องยืนยันเงื่อนไขกับปลายทางก่อนดำเนินการ",
      },
      {
        q: "ปลายทางขอข้อความเฉพาะทำอย่างไร",
        a: "ขอตัวอย่างข้อความจากหน่วยงานปลายทางแล้วนำไปหารือกับนายทะเบียนก่อนยื่นคำขอ เพราะรูปแบบและถ้อยคำที่นายทะเบียนออกได้เป็นไปตามระเบียบของทางราชการ",
      },
      {
        q: "เคยสมรสมาก่อนต้องใช้เอกสารใดเพิ่ม",
        a: "โดยทั่วไปต้องแนบทะเบียนหย่าหรือหลักฐานการสิ้นสุดการสมรสก่อนหน้า พร้อมคำแปลและการรับรองตามที่ปลายทางกำหนด",
      },
      {
        q: "หนังสือรับรองรายการทะเบียนราษฎรใช้แทนทะเบียนบ้านได้ไหม",
        a: "ใช้แทนได้ในหลายกรณีที่ปลายทางต้องการเพียงการยืนยันรายการ เช่น ที่อยู่หรือวันเกิด แต่บางแห่งยังขอสำเนาหน้าทะเบียนบ้านควบคู่ ควรยืนยันก่อนยื่น",
      },
      {
        q: "ต้องการให้ช่วยประเมินกรณีของเราติดต่ออย่างไร",
        a: "ติดต่อ LINE @nycli โทร +66-83-249-4999 หรืออีเมล nycli@ilc.ltd แจ้งประเทศปลายทางและวัตถุประสงค์ ทีมงานจะแจ้งเอกสารที่ต้องเตรียมและลำดับการรับรองที่เหมาะสม",
      },
    ],
    faqsEn: [
      {
        q: "Where is a single-status certificate obtained?",
        a: "At the district or local registration office with jurisdiction. The registrar issues it from the civil and family registration databases.",
      },
      {
        q: "How long is a status certificate valid?",
        a: "The document carries no expiry of its own, but most receivers require one issued within a recent period, so confirm the window before you start.",
      },
      {
        q: "What if the receiver prescribes specific wording?",
        a: "Get a sample from the receiver and discuss it with the registrar before applying; the registrar can only issue formats permitted by official regulations.",
      },
      {
        q: "What extra documents are needed after a prior marriage?",
        a: "Usually the divorce registration or other evidence that the earlier marriage ended, with translation and legalisation as the receiver requires.",
      },
      {
        q: "Can a certificate of particulars replace the house registration?",
        a: "Often yes where the receiver only needs a fact confirmed, such as address or date of birth, but some still ask for the page copy as well. Confirm first.",
      },
      {
        q: "How do we get our case assessed?",
        a: "Contact LINE @nycli, +66-83-249-4999 or nycli@ilc.ltd with the destination country and purpose, and we will set out the documents and certification order.",
      },
    ],
  },
];
